// ADR 24.9.26h §4 — the account-scoped storage that `signOutLocally()` does not
// itself own, forgotten on the same event.
//
// The event rather than `sessionTransition`: `lingara:signed-out` fires from
// `signOutLocally()`, which the voluntary sign-out and every refused refresh
// reach — including a cold start's bootstrap refresh, where the session is
// still `LOADING` with `userId: null` and `sessionTransition` wipes nothing.
// That is the shared-browser case this exists for: the learner walked away,
// the token lapsed, and the next person opens the tab. The keys wiped here are
// not per-user, so the listener needs no user id. It mirrors
// `usePurgeOnSignOut` (`offline/OfflineCache.tsx`) rather than registering
// through its `onPurge`, which is the seam for in-memory state.
//
// Deliberately kept: the device token (11.9.26i), the offline queue (23.9.26w
// §8), consent and UI preferences — none of them is the account's data.

import { useEffect } from 'react'

//
// ADR 30.9.26ai §11 — an installed app's cached cards (`lingara-apps-v1`),
// the slot choices and the session's removed-install set join: a third
// party's card and an install id are both the account's.

import { clearAppCards } from '../features/apps/cardCache'
import { clearRemovedInstalls } from '../features/apps/removedInstalls'
import { clearSlotChoices } from '../features/apps/slotChoice'
import { clearPlaygroundDraft } from '../features/builder/playground/scratchDraft'
import { clearWalkthroughProgress } from '../features/walkthrough/progressStore'
import { SIGNED_OUT_EVENT } from './tokenStore'

// ADR 8.10.26h §7 — the builder playground's per-tab draft joins: a script
// and its sample are the developer's, and a lapsed tab may be reused.

/** Clear account-scoped browser storage on every sign-out, voluntary or not. */
export function useAccountStorageWipe(): void {
  useEffect(() => {
    const onSignedOut = () => {
      clearWalkthroughProgress()
      void clearAppCards()
      clearSlotChoices()
      clearRemovedInstalls()
      clearPlaygroundDraft()
    }
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut)
  }, [])
}
